import React from "react";
import classNames from "classnames";

import ClipLoader from 'react-spinners/ClipLoader';
import Typography from '@material-ui/core/Typography';

import API from "@beardeddevops/react.api";
import Framework from "@beardeddevops/react.framework";

import moment from "moment";
import Types from 'constants/Types';


class SubscriptionObject extends Framework.Components.Object {

	constructor(props) {
		/**
		 *  @typedef {Accounts_Subscriptions_Object} this.state
		 */
		super(props, Types.Accounts_Subscriptions._name);
		this.state.card_error = false;
	}

	loadSelfAfter(data) {
		if (this.props.rootState.fflboss_pos_combo) {
			this.setState({card_error: false});
			if (typeof this.props.setCardError === 'function') {
				this.props.setCardError(false);
			}
			return;
		}

		// API owns pricing; only copy display fields that may not live on model.properties.
		const nextState = {};
		if (data) {
			if (data.plan_name != null) {
				nextState.plan_name = data.plan_name;
			}
			if (data.monthly_amount != null && data.monthly_amount !== '') {
				nextState.monthly_amount = data.monthly_amount;
			}
			if (data.quantity != null) {
				nextState.quantity = data.quantity;
			}
		}
		if (Object.keys(nextState).length > 0) {
			this.setState(nextState);
		}

		this.loadBilling().then();
		if (!this.props.rootState.no_subscription) {
			this.loadTerminals().then();
		}
	}

	loadTerminals = async () => {
		const terminals = new API.BuildClass(Types.Accounts_Subscriptions._name, API.RequestType.READ_ALL);
		terminals.url = "/api/terminals/check";
		const data = await terminals.submit(null, API.RequestType.READ);
		const nextQuantity = data.total ?? data.allowed ?? data.quantity;
		if (nextQuantity != null && Number(nextQuantity) > 0) {
			this.setState({quantity: nextQuantity});
		}
	}

	loadBilling = async () => {
		const billing = new API.BuildClass(Types.Accounts_Billing._name, API.RequestType.READ);
		try {
			const data = await billing.submit(null, API.RequestType.READ);
			this.setState({
				card_brand: data.brand,
				card_exp_date: data.exp_month + "/" + data.exp_year.toString().slice(2, 4),
				card_last4: data.last4,
				card_error: false
			});
			if (typeof this.props.cardExists === 'function') {
				this.props.cardExists(data.last4 ?? false);
			}
			if (typeof this.props.setCardError === 'function') {
				this.props.setCardError(false);
			}
		} catch (e) {
			this.setState({card_error: true});
			if (typeof this.props.setCardError === 'function') {
				this.props.setCardError(true);
			}
		}
	}

	formatCostLabel = (monthlyAmount) => {
		if (monthlyAmount == null || monthlyAmount === '') {
			return null;
		}
		const amount = Number(monthlyAmount);
		if (Number.isNaN(amount)) {
			return null;
		}
		return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
	}

	renderRenewalLine = (trial_end, current_period_end) => {
		if (!trial_end && !current_period_end) {
			return null;
		}
		const isTrialEnded = trial_end && moment.unix(trial_end).isBefore(moment());
		return (
			<div
				style={{
					textAlign: "center",
					fontSize: "12px",
					color: isTrialEnded ? "red" : "black"
				}}
			>
				{trial_end ? (
					isTrialEnded
						? <>Trial ended on {moment.unix(trial_end).format("MM/DD/YYYY")}</>
						: <>Trial ends on {moment.unix(trial_end).format("MM/DD/YYYY")}</>
				) : (
					<>Renews on {moment.unix(current_period_end).format("MM/DD/YYYY")}</>
				)}
				<br/><br/>
			</div>
		);
	}

	renderCardNotice = (trial_end, card_error) => {
		if (trial_end && card_error) {
			return (
				<p style={{textAlign: "center", fontSize: "18px"}}>
					<strong>When your free trial ends, you will be required to provide a valid credit card.</strong>
				</p>
			);
		}
		if (trial_end && moment.unix(trial_end).isAfter(moment())) {
			return (
				<p style={{textAlign: "center", fontSize: "18px"}}>
					<strong>You will be billed at the end of your free trial.</strong>
				</p>
			);
		}
		if (!trial_end && card_error) {
			return (
				<p style={{textAlign: "center", fontSize: "18px"}}>
					<strong>You must have a valid card number.</strong>
				</p>
			);
		}
		return null;
	}

	renderCostHeading = (exists, costLabel) => {
		if (!exists) {
			return (
				<h4 style={{textAlign: "center", margin: "0 0 10px"}}>
					You have no active subscriptions. Please subscribe below. Contact support if you have questions or concerns.
				</h4>
			);
		}
		return (
			<h4 style={{textAlign: "center", margin: "0 0 10px"}}>
				{costLabel !== null
					? `Your cost is $${costLabel}/mo`
					: 'Your cost is based on your active subscription'}
			</h4>
		);
	}

	render() {
		const {id, current_period_end, quantity, loading, plan, plan_name, card_error, trial_end, monthly_amount} = this.state;
		// Multi-item subs (e.g. Terminals + FFL Boss bundle) come back with Stripe's legacy
		// top-level `plan` = null, and `id` isn't in the OPTIONS schema so the framework
		// never hydrates it — fall back to fields loadSelfAfter always copies on a real read.
		const exists = !!(id || plan || plan_name || monthly_amount);
		const isTacticalAccount = !!(this.props.rootState.is_tactical || this.props.rootState.isTactical);
		const costLabel = this.formatCostLabel(monthly_amount);
		const isTerminalsPlan = typeof plan_name === 'string' && plan_name.indexOf('Terminals') !== -1;

		if (this.props.rootState.fflboss_pos_combo) {
			return (
				<div>
					{exists ? (
						<div style={{display: 'flex', justifyContent: 'space-between'}}>
							<Typography variant="h5" component="h3">Subscription</Typography>
							<Typography variant="h5" component="h3">
								Current Plan: <b>{plan_name}</b>
							</Typography>
						</div>
					) : (
						<Typography variant="h5" component="h3">Subscription</Typography>
					)}
					<br/>
					<hr/>
					<h4 style={{textAlign: "center", margin: "0 0 10px"}}>
						Your POS subscription is managed through FFL Boss.
					</h4>
					<p style={{textAlign: "center", fontSize: "14px", margin: 0}}>
						For billing updates, plan changes, or subscription questions, please sign in to FFL Boss and manage everything there.
					</p>
				</div>
			);
		}

		if (isTacticalAccount) {
			return (
				<div>
					<Typography variant="h5" component="h3">Subscription</Typography>
					<br/>
					{this.state.card_brand && (
						<div className={"text-center"}>
							<i>Current Card is a {this.state.card_brand} ending in {this.state.card_last4} Expires on {this.state.card_exp_date}</i>
						</div>
					)}
					<hr/>
					{this.renderCostHeading(exists, costLabel)}
					{exists ? this.renderRenewalLine(trial_end, current_period_end) : null}
					{exists && isTerminalsPlan && (
						<p style={{textAlign: "center", fontSize: "12px"}}>
							<b>$50/mo</b> for each additional Terminal connection after the first 1
						</p>
					)}
					{this.renderCardNotice(trial_end, card_error)}
					<div className={classNames("loader", {'active': loading})}>
						<ClipLoader color={"#4A4A4A"} loading={loading}/>
					</div>
				</div>
			);
		}

		return (
			<div>
				{exists ? (
					<div style={{display: 'flex', justifyContent: 'space-between'}}>
						<Typography variant="h5" component="h3">Subscription</Typography>
						<Typography variant="h5" component="h3">
							Current Plan: <b>{plan_name}</b>
						</Typography>
					</div>
				) : (
					<Typography variant="h5" component="h3">Subscription</Typography>
				)}
				<br/>
				{this.state.card_brand && (
					<div className={"text-center"}>
						<i>Current Card is a {this.state.card_brand} ending in {this.state.card_last4} Expires on {this.state.card_exp_date}</i>
					</div>
				)}
				<hr/>
				{exists && isTerminalsPlan && (
					<p style={{textAlign: "center", margin: "0 0 0 10px"}}>
						You have {quantity != null ? quantity : 0} active terminal(s) *
					</p>
				)}
				{this.renderCostHeading(exists, costLabel)}
				{exists ? this.renderRenewalLine(trial_end, current_period_end) : null}
				{!exists && (
					<p style={{textAlign: "center", fontSize: "12px"}}>
						<b>$75/mo</b> for <i style={{fontSize: "14px", fontStyle: 'italic'}}>Cash Only</i>
						<br/>
						<b>$150/mo</b> for 1 Terminal connection<br/>
						<b>$50/mo</b> for each additional Terminal connection
					</p>
				)}
				{exists && isTerminalsPlan && (
					<p style={{textAlign: "center", fontSize: "12px"}}>
						<b>$50/mo</b> for each additional Terminal connection
					</p>
				)}
				{exists && isTerminalsPlan && this.props.rootState.api_key_exists && (
					<p style={{textAlign: "center", fontSize: "12px"}}>
						<strong>* Your Subscription will automatically adjust to the number of<br/>Dejavoo z11s that are
							active with your payment processor.</strong>
					</p>
				)}
				{!this.props.rootState.api_key_exists && isTerminalsPlan && (
					<p style={{textAlign: "center", fontSize: "14px"}}>
						<strong>*To add terminals, update the API key under Account Settings*</strong>
					</p>
				)}
				{this.renderCardNotice(trial_end, card_error)}
				<div className={classNames("loader", {'active': loading})}>
					<ClipLoader color={"#4A4A4A"} loading={loading}/>
				</div>
			</div>
		);
	}
}

export default Framework.Components.withRootState(SubscriptionObject);
